import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Separator } from './ui/separator';
import { Star, Users, Plus, Minus } from 'lucide-react';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingWidget = ({ property }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState({ adults: 2, children: 0, infants: 0 });
  const [showGuestPopover, setShowGuestPopover] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/host/availability/${property.id || property._id}`);
        if (res.data.success && res.data.all_unavailable) {
          setUnavailableDates(res.data.all_unavailable);
        }
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      }
    };
    if (property?.id || property?._id) fetchAvailability();
  }, [property]);

  const isDateUnavailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return unavailableDates.includes(dateStr);
  };

  const isCheckoutDisabled = (date) => {
    if (!checkIn || date <= checkIn) return true;
    if (isDateUnavailable(date)) return true;
    const dayAfterCheckin = new Date(checkIn);
    dayAfterCheckin.setDate(dayAfterCheckin.getDate() + 1);
    let current = new Date(dayAfterCheckin);
    while (current < date) {
      if (isDateUnavailable(current)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const handleCheckInSelect = (date) => {
    setCheckIn(date);
    // Reset check-out if it's before or equal to new check-in
    if (checkOut && date && checkOut <= date) {
      setCheckOut(null);
    }
    setCheckInOpen(false);
    // Auto-open check-out picker
    setTimeout(() => setCheckOutOpen(true), 200);
  };

  const handleCheckOutSelect = (date) => {
    setCheckOut(date);
    setCheckOutOpen(false);
  };

  const updateGuests = (type, operation) => {
    setGuests(prev => ({
      ...prev,
      [type]: operation === 'add' ? prev[type] + 1 : Math.max(0, prev[type] - 1)
    }));
  };

  const totalGuests = guests.adults + guests.children + guests.infants;
  const maxGuests = property.guests || 10;
  const canAddMoreGuests = totalGuests < maxGuests;
  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const total = nights * property.price;

  const handleReserve = () => {
    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }
    if (!isAuthenticated) {
      toast.info('Please login or sign up to book a property');
      navigate('/login');
      return;
    }
    const checkInStr = format(checkIn, 'yyyy-MM-dd');
    const checkOutStr = format(checkOut, 'yyyy-MM-dd');
    navigate(`/booking/${property.id}?checkIn=${checkInStr}&checkOut=${checkOutStr}&guests=${totalGuests}`);
  };

  return (
    <Card className="border border-gray-300 rounded-xl shadow-xl">
      <CardHeader>
        <div className="flex items-baseline justify-between">
          <div>
            <span className="text-2xl font-semibold">₹{property.price.toLocaleString()}</span>
            <span className="text-gray-600"> / night</span>
          </div>
          <div className="flex items-center space-x-1">
            <Star className="h-4 w-4 fill-current text-gray-900" />
            <span className="font-medium">{property.rating}</span>
            <span className="text-gray-500">·</span>
            <button className="underline text-gray-600 hover:text-gray-900">
              {property.reviewCount || 0} reviews
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Check-in / Check-out */}
          <div className="grid grid-cols-2 border border-gray-300 rounded-lg overflow-hidden">
            <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
              <PopoverTrigger asChild>
                <button
                  className="p-3 text-left border-r hover:bg-gray-50 transition-colors"
                  data-testid="checkin-trigger"
                >
                  <div className="text-xs font-semibold uppercase">Check-in</div>
                  <div className="text-sm text-gray-600">
                    {checkIn ? format(checkIn, 'MMM dd, yyyy') : 'Add date'}
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkIn}
                  onSelect={handleCheckInSelect}
                  defaultMonth={new Date()}
                  disabled={(date) => date < startOfDay(new Date()) || isDateUnavailable(date)}
                />
              </PopoverContent>
            </Popover>

            <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
              <PopoverTrigger asChild>
                <button
                  className="p-3 text-left hover:bg-gray-50 transition-colors"
                  data-testid="checkout-trigger"
                >
                  <div className="text-xs font-semibold uppercase">Check-out</div>
                  <div className="text-sm text-gray-600">
                    {checkOut ? format(checkOut, 'MMM dd, yyyy') : 'Add date'}
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  key={checkIn ? checkIn.toISOString() : 'no-checkin'}
                  mode="single"
                  selected={checkOut}
                  onSelect={handleCheckOutSelect}
                  defaultMonth={checkIn || new Date()}
                  disabled={isCheckoutDisabled}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Guests */}
          <Popover open={showGuestPopover} onOpenChange={setShowGuestPopover}>
            <PopoverTrigger asChild>
              <button
                className="w-full p-3 text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="guests-trigger"
              >
                <div className="text-xs font-semibold uppercase">Guests</div>
                <div className="text-sm text-gray-600 flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  {totalGuests} guest{totalGuests !== 1 ? 's' : ''}
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-4">
                {/* Adults */}
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <div className="font-semibold">Adults</div>
                    <div className="text-sm text-gray-500">Ages 13 or above</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => updateGuests('adults', 'subtract')} disabled={guests.adults <= 1}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center">{guests.adults}</span>
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => updateGuests('adults', 'add')} disabled={!canAddMoreGuests}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Children */}
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <div className="font-semibold">Children</div>
                    <div className="text-sm text-gray-500">Ages 2-12</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => updateGuests('children', 'subtract')} disabled={guests.children <= 0}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center">{guests.children}</span>
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => updateGuests('children', 'add')} disabled={!canAddMoreGuests}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Infants */}
                <div className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-semibold">Infants</div>
                    <div className="text-sm text-gray-500">Under 2</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => updateGuests('infants', 'subtract')} disabled={guests.infants <= 0}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center">{guests.infants}</span>
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                      onClick={() => updateGuests('infants', 'add')} disabled={!canAddMoreGuests}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-gray-500 pt-2">
                  This property allows a maximum of {maxGuests} guest{maxGuests !== 1 ? 's' : ''}
                </p>
              </div>
            </PopoverContent>
          </Popover>

          {/* Reserve Button */}
          {property.host_verified === false ? (
            <div className="space-y-2" data-testid="reserve-disabled-block">
              <Button
                className="w-full py-6 text-base font-semibold rounded-lg cursor-not-allowed"
                disabled
                data-testid="reserve-btn"
              >
                Reserve
              </Button>
              <p
                className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5 text-center"
                data-testid="host-kyc-notice"
              >
                <strong>Host KYC in progress.</strong> This host is completing identity verification.
                Booking will open as soon as it&apos;s approved.
              </p>
            </div>
          ) : (
            <Button
              className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white text-base font-semibold rounded-lg"
              onClick={handleReserve}
              data-testid="reserve-btn"
            >
              Reserve
            </Button>
          )}

          {nights > 0 && (
            <>
              <p className="text-center text-sm text-gray-500">You won't be charged yet</p>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="underline">₹{property.price.toLocaleString()} x {nights} nights</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingWidget;
